import { useEffect } from "react";
import { DEFAULT_SOCIAL_IMAGE, SITE_NAME, buildSeoMetadata, getCanonicalUrl } from "@/lib/seo";

interface SEOConfig {
  path: string;
  title: string;
  description: string;
  lang?: "sv" | "en";
  image?: string;
  noindex?: boolean;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

/**
 * Hook to set comprehensive SEO meta tags for a page
 */
export const useSEO = ({
  path,
  title,
  description,
  lang = "sv",
  image = DEFAULT_SOCIAL_IMAGE,
  noindex = false,
  jsonLd,
}: SEOConfig) => {
  useEffect(() => {
    const metadata = buildSeoMetadata({
      path,
      title,
      description,
      lang,
      image,
      noindex,
    });

    // Set document title
    document.title = metadata.title;

    // Set html lang attribute
    document.documentElement.lang = metadata.lang;

    // Helper to set or create meta tag
    const setMeta = (selector: string, attribute: string, value: string) => {
      let tag = document.querySelector(selector) as HTMLMetaElement;
      if (!tag) {
        tag = document.createElement("meta");
        const attrName = selector.includes("property=") ? "property" : "name";
        const attrValue = selector.match(/["']([^"']+)["']/)?.[1] || "";
        tag.setAttribute(attrName, attrValue);
        document.head.appendChild(tag);
      }
      tag.setAttribute(attribute, value);
    };

    // Canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonicalLink) {
      canonicalLink = document.createElement("link");
      canonicalLink.rel = "canonical";
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = metadata.canonicalUrl;

    // Meta description
    setMeta('meta[name="description"]', "content", metadata.description);

    // Open Graph tags
    setMeta('meta[property="og:title"]', "content", metadata.title);
    setMeta('meta[property="og:description"]', "content", metadata.description);
    setMeta('meta[property="og:url"]', "content", metadata.ogUrl);
    setMeta('meta[property="og:site_name"]', "content", SITE_NAME);
    setMeta('meta[property="og:image"]', "content", metadata.image);
    setMeta('meta[property="og:locale"]', "content", metadata.ogLocale);

    // Twitter tags
    setMeta('meta[name="twitter:card"]', "content", "summary_large_image");
    setMeta('meta[name="twitter:title"]', "content", metadata.title);
    setMeta('meta[name="twitter:description"]', "content", metadata.description);
    setMeta('meta[name="twitter:image"]', "content", metadata.image);

    // Robots
    if (metadata.noindex) {
      setMeta('meta[name="robots"]', "content", metadata.robots || "noindex, nofollow");
    } else {
      const robotsTag = document.querySelector('meta[name="robots"]');
      if (robotsTag) robotsTag.remove();
    }

    // JSON-LD structured data (page-specific)
    let jsonLdScript = document.querySelector('script[data-page-jsonld]') as HTMLScriptElement;
    if (jsonLd) {
      if (!jsonLdScript) {
        jsonLdScript = document.createElement("script");
        jsonLdScript.type = "application/ld+json";
        jsonLdScript.setAttribute("data-page-jsonld", "true");
        document.head.appendChild(jsonLdScript);
      }
      // Support both single objects and arrays of objects (for multiple schema types)
      if (Array.isArray(jsonLd)) {
        jsonLdScript.textContent = JSON.stringify({
          "@context": "https://schema.org",
          "@graph": jsonLd,
        });
      } else {
        jsonLdScript.textContent = JSON.stringify({
          "@context": "https://schema.org",
          ...jsonLd,
        });
      }
    } else if (jsonLdScript) {
      jsonLdScript.remove();
    }

    return () => {
      // Reset lang to default on unmount
      document.documentElement.lang = "sv";
      // Remove page-specific JSON-LD
      const pageJsonLd = document.querySelector('script[data-page-jsonld]');
      if (pageJsonLd) pageJsonLd.remove();
    };
  }, [path, title, description, lang, image, noindex, jsonLd]);
};

/**
 * Simple hook to just set canonical URL (backwards compatible)
 */
export const useCanonical = (path: string) => {
  useEffect(() => {
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonicalLink) {
      canonicalLink = document.createElement("link");
      canonicalLink.rel = "canonical";
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = getCanonicalUrl(path);

    return () => {
      if (canonicalLink) {
        canonicalLink.remove();
      }
    };
  }, [path]);
};

export default useCanonical;
